import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { HclSnippet } from '@/shared/components/HclSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import { CurlSecretNote, CliCiNote } from '@/features/integrations/components/GuideNotes'
import {   CLI_INSTALL_GITHUB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS, TOKEN_EXCHANGE_CLI } from '@/features/integrations/snippets'

// ── Module Consumption ────────────────────────────────────────────────────────
export function ModuleConsumptionGuide({ org, token, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('cli')
  const orgSlug  = org?.slug ?? '<org-slug>'
  const tokenVal = token ?? '<bot token>'

  // Module sources are 4-part: host / org-slug / name / provider. Unlike
  // providers, the org slug IS in the source - the registry resolves the module
  // by joining the namespace segment against tenants.slug.
  const moduleBlock = `module "database" {
  source  = "registry.terramantle.dev/${orgSlug}/my-module/aws"
  version = "1.0.0"

  # module inputs go here
}`

  const terraformrc = `# ~/.terraformrc  (Windows: %APPDATA%\\terraform.rc)
credentials "registry.terramantle.dev" {
  token = "${tokenVal}"
}`

  const cliSnippet = `terramantle auth login

# Find a module and check its Trust verdict before you depend on it
terramantle modules search database
terramantle modules show ${orgSlug}/my-module/aws

# Exports TF_TOKEN_registry_terramantle_dev for you - no ~/.terraformrc needed
eval "$(terramantle auth env --terraform)"

tofu init   # or: terraform init`

  const curlSnippet = `${TOKEN_EXCHANGE_CLI}

# Option A - CLI config (~/.terraformrc), best for local / interactive use
cat > ~/.terraformrc <<EOF
credentials "registry.terramantle.dev" {
  token = "${tokenVal}"
}
EOF

# Option B - environment variable, best for CI
#   The host's dots become underscores: registry.terramantle.dev -> registry_terramantle_dev
export TF_TOKEN_registry_terramantle_dev=${tokenVal}

tofu init   # or: terraform init`

  const githubSnippet = `name: terraform

on:
  push:
    branches: [main]

permissions:
  contents: read
  id-token: write   # lets terramantle use the runner's ambient OIDC token

jobs:
  apply:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      - name: Terraform init & apply
        run: |
          # Sets TF_TOKEN_registry_terramantle_dev, which tofu sends as a Bearer token
          eval "$(terramantle auth env --terraform)"
          tofu init
          tofu apply -auto-approve`

  const gitlabSnippet = `stages:
  - deploy

apply:
  stage: deploy
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
${CLI_GITLAB_SETUP}
  script:
    - eval "$(terramantle auth env --terraform)"
    - tofu init
    - tofu apply -auto-approve
  only:
    - main`

  return (
    <div>
      <h3 style={s.guideTitle}>Consuming Modules</h3>
      <p style={s.para}>
        Terramantle serves your organisation's modules over the standard Terraform module
        registry protocol. Reference one with a <code style={s.code}>source</code> and{' '}
        <code style={s.code}>version</code> in a <code style={s.code}>module</code> block, then
        authenticate. Need to publish one first? See{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('module-publishing')}>Publishing Module</button>.
      </p>

      <h3 style={s.subTitle}>1. Reference the module</h3>
      <p style={s.para}>
        Source addresses are <code style={s.code}>registry.terramantle.dev/&lt;org-slug&gt;/&lt;name&gt;/&lt;provider&gt;</code> -
        host, then your organisation slug, then the module name and provider. Unlike providers,
        the org slug <strong>is</strong> part of the source path.
      </p>
      <HclSnippet code={moduleBlock} copyText={moduleBlock} />

      <h3 style={s.subTitle}>2. Authenticate and initialise</h3>
      <p style={s.para}>
        The <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>{' '}
        exports the right <code style={s.code}>TF_TOKEN_*</code> variable for you, locally and in CI. Setting it by
        hand works too - either a <code style={s.code}>credentials</code> block in your CLI config, or the
        environment variable (the host's dots become underscores). Both are read natively by Terraform and
        OpenTofu - no <code style={s.code}>TF_HTTP_PASSWORD</code> here, that is for the state backend only.
      </p>
      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'curl' && (
        <>
          <p style={{ ...s.para, marginTop: 10 }}>For local use, a <code style={s.code}>credentials</code> block is the alternative to the env var:</p>
          <HclSnippet code={terraformrc} copyText={terraformrc} />
          <CurlSecretNote />
        </>
      )}
      {(platform === 'github' || platform === 'gitlab') && <CliCiNote onSwitchGuide={onSwitchGuide} />}

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>The namespace in the source is your organisation <strong>slug</strong> (e.g. <code style={s.code}>{orgSlug}</code>), not its display name.</li>
        <li>Private modules require a token with module read access; public modules resolve anonymously.</li>
        <li>Only <code style={s.code}>consumable</code> versions (passing scans and policy) resolve - blocked versions return an error.</li>
        <li><code style={s.code}>version</code> follows semver and accepts constraints like <code style={s.code}>~&gt; 1.0</code>.</li>
      </ul>
    </div>
  )
}
